import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Users, Crown, CheckCircle, Clock, MapPin } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { projectAPI, chatAPI, createQueryKey } from '../services/api';
import { getImageUrl } from '../config/runtime';
import InviteTeamCard from './InviteTeamCard';
import CompanionJoinPanel from './CompanionJoinPanel';
import { useAuth } from '../contexts/AuthContext';

const ParticipantsModal = ({ isOpen, onClose, projectId, project }) => {
  const { user, updateProfile } = useAuth();
  const queryClient = useQueryClient();
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);

  // Fetch fresh project data when modal opens to ensure objectives are up-to-date
  const { data: freshProjectResponse, refetch: refetchProject } = useQuery({
    queryKey: createQueryKey.project(projectId),
    queryFn: async () => {
      const response = await projectAPI.getById(projectId);
      return response;
    },
    enabled: !!projectId && isOpen,
    staleTime: 0, // Always fetch fresh when modal opens
    refetchOnMount: true,
    cacheTime: 0, // Don't cache - always fetch fresh
  });

  // Force refetch when modal opens to ensure we have latest objectives
  useEffect(() => {
    if (isOpen && projectId) {
      refetchProject();
    }
  }, [isOpen, projectId, refetchProject]);

  // Use fresh project data if available, otherwise fall back to prop
  // Handle both response structures: response.data.project (wrapped) or response.data (direct)
  const freshProject = freshProjectResponse?.data?.project || freshProjectResponse?.data;
  const currentProject = freshProject || project;
  
  const isSiteProject = true;
  const leaderboard = [];
  const isCompetitive = false;

  // Fetch participants (returns all team members)
  // No polling needed - WebSocket events (participant_joined/participant_left) update this cache
  const { data: participantsResponse, isLoading } = useQuery({
    queryKey: ['participants', projectId],
    queryFn: () => projectAPI.getParticipants(projectId),
    enabled: !!projectId && isOpen,
  });

  // Fetch chats for Site projects to filter active participants
  // No polling needed - WebSocket events invalidate this query when participants change
  const { data: chatsResponse, isLoading: isChatsLoading } = useQuery({
    queryKey: createQueryKey.chats(projectId),
    queryFn: () => chatAPI.getByProject(projectId),
    enabled: !!projectId && isOpen && isSiteProject,
  });

  // Fetch active WebSocket users (users currently online) for all projects
  // No polling needed - WebSocket connection itself indicates presence, and participant_joined/left events provide updates
  const { data: activeWebSocketUsersResponse } = useQuery({
    queryKey: ['active-websocket-users', projectId],
    queryFn: () => projectAPI.getActiveWebSocketUsers(projectId),
    enabled: !!projectId && isOpen,
  });

  const isLeaderboardLoading = false;

  // Multiplayer privacy: "share my live position with the group" is a per-user,
  // per-run choice stored on the user's OWN chat (default ON; the backend
  // suppresses the location broadcast when off — the trail still records).
  const myChat = useMemo(() => {
    const chats = chatsResponse?.data?.data?.chats || [];
    return (
      chats.find((c) => c.userId === user?.id && c.isActive === true && !c.archivedAt) || null
    );
  }, [chatsResponse, user?.id]);

  const [shareLocation, setShareLocation] = useState(true);
  useEffect(() => {
    if (myChat) {
      setShareLocation(myChat.metadata?.share_live_location !== false);
    }
  }, [myChat]);

  const toggleShareLocation = async () => {
    if (!myChat) return;
    const next = !shareLocation;
    setShareLocation(next); // optimistic — a privacy switch must feel instant
    try {
      await chatAPI.updateChat(myChat.id, { share_live_location: next });
      queryClient.invalidateQueries(createQueryKey.chats(projectId));
    } catch (err) {
      console.error('Failed to update location sharing:', err);
      setShareLocation(!next);
    }
  };

  // Guests are all minted as "Guest" — an anonymous pin is useless with 3+
  // people, so the sharing row doubles as the name-capture moment.
  const hasGenericName =
    user?.authProvider === 'guest' && (!user?.firstName || user.firstName === 'Guest');
  const [mapName, setMapName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const saveMapName = async () => {
    const trimmed = mapName.trim();
    if (!trimmed || savingName) return;
    setSavingName(true);
    try {
      await updateProfile({ firstName: trimmed });
    } catch (err) {
      console.error('Failed to save name:', err);
    } finally {
      setSavingName(false);
    }
  };
  
  // Filter participants for ALL projects to ensure consistency
  // (Only show users with active sessions/chats, which excludes left/removed users)
  const participants = useMemo(() => {
    // Extract all participants from the response
    let allParticipants = participantsResponse?.data?.data?.participants || participantsResponse?.data?.participants || [];
    
    // For site projects, we also need to check active chats to include users who have sessions
    // but might not have Collaboration records (e.g., owner)
    let activeChatUsers = [];
    if (isSiteProject && chatsResponse && !isChatsLoading) {
    const chats = chatsResponse?.data?.data?.chats || [];
      const activeChats = chats.filter(c => c.isActive === true && !c.archivedAt);
      
      // Get user info from active chats
      activeChatUsers = activeChats.map(chat => {
        // Try to find user in existing participants first
        const existingParticipant = allParticipants.find(p => p.id === chat.userId);
        if (existingParticipant) {
          return existingParticipant;
        }
        
        // If not found, create a basic user object from chat data
        // Note: chat might have user info, or we might need to use project owner
        const projectToUse = currentProject || project;
        if (chat.userId === projectToUse?.ownerId && projectToUse?.owner) {
          return {
            id: projectToUse.owner.id || projectToUse.ownerId,
            username: projectToUse.owner.username || projectToUse.owner.email,
            firstName: projectToUse.owner.firstName,
            lastName: projectToUse.owner.lastName,
            avatar: projectToUse.owner.avatar,
            role: 'ADMIN'
          };
        }
        
        // Fallback: create minimal user object
        return {
          id: chat.userId,
          username: chat.user?.username || chat.user?.email || 'Unknown',
          firstName: chat.user?.firstName,
          lastName: chat.user?.lastName,
          avatar: chat.user?.avatar,
          role: chat.userId === projectToUse?.ownerId ? 'ADMIN' : 'MEMBER'
        };
      }).filter(Boolean);
    }
    
    // Merge participants from API and active chat users
    const participantsMap = new Map();
    
    // Add all participants from API
    allParticipants.forEach(p => {
      if (p.id) participantsMap.set(p.id, p);
    });
    
    // Add/update with active chat users (this ensures users with active chats are included)
    activeChatUsers.forEach(u => {
      if (u.id) {
        // Update if exists, or add if new
        const existing = participantsMap.get(u.id);
        if (existing) {
          // Merge data, prefer existing participant data
          participantsMap.set(u.id, { ...existing, ...u });
        } else {
          participantsMap.set(u.id, u);
        }
      }
    });
    
    // Ensure the project owner is always included
    const projectToUse = currentProject || project;
    if (projectToUse?.ownerId && projectToUse?.owner) {
      const ownerInMap = participantsMap.has(projectToUse.ownerId);
      if (!ownerInMap) {
        participantsMap.set(projectToUse.ownerId, {
          id: projectToUse.owner.id || projectToUse.ownerId,
          username: projectToUse.owner.username || projectToUse.owner.email,
          firstName: projectToUse.owner.firstName,
          lastName: projectToUse.owner.lastName,
          avatar: projectToUse.owner.avatar,
          role: 'ADMIN'
        });
      }
    }
    
    let finalParticipants = Array.from(participantsMap.values());

    // ROSTER, not presence: keep everyone with an active (unarchived) chat on
    // this run — someone whose phone is locked is still on the outing. Leaving
    // the run archives the chat, which is what shrinks this list.
    if (isSiteProject && chatsResponse && !isChatsLoading) {
      const chats = chatsResponse?.data?.data?.chats || [];
      const activeChatUserIds = new Set(
        chats
          .filter(c => c.isActive === true && !c.archivedAt)
          .map(c => c.userId)
          .filter(Boolean)
      );

      // The owner is on their own run even before their chat exists
      const projectToUse = currentProject || project;
      if (projectToUse?.ownerId) {
        activeChatUserIds.add(projectToUse.ownerId);
      }

      if (activeChatUserIds.size > 0) {
        finalParticipants = finalParticipants.filter(p => activeChatUserIds.has(p.id));
      }
    }

    // Presence is a per-person annotation (green/grey dot), never a filter.
    const activeWebSocketUserIds = new Set(
      activeWebSocketUsersResponse?.data?.data?.activeUserIds || []
    );
    return finalParticipants.map(p => ({
      ...p,
      isOnline: activeWebSocketUserIds.has(p.id),
    }));
  }, [isSiteProject, participantsResponse, chatsResponse, isChatsLoading, currentProject, project, activeWebSocketUsersResponse]);

  const onlineCount = useMemo(
    () => participants.filter(p => p.isOnline).length,
    [participants]
  );
  
  // Debug logging (separate useEffect to avoid dependency issues)
  useEffect(() => {
    if (isOpen) {
      const allParticipantsRaw = participantsResponse?.data?.data?.participants || participantsResponse?.data?.participants || [];
      const chats = chatsResponse?.data?.data?.chats || [];
      const activeChats = chats.filter(c => c.isActive === true && !c.archivedAt);
      
      console.log('🔍 ParticipantsModal Debug:', {
        'API Response': participantsResponse,
        'Raw Participants from API': allParticipantsRaw,
        'Participants Count': allParticipantsRaw.length,
        'Final Participants': participants,
        'Final Count': participants.length,
        'Is Site Project': isSiteProject,
        'Chats Response': chatsResponse,
        'All Chats': chats.length,
        'Active Chats': activeChats.length,
        'Active Chat User IDs': activeChats.map(c => c.userId),
        'Is Chats Loading': isChatsLoading,
        'Project Owner ID': project?.ownerId,
        'Current User ID': user?.id,
        'Is Owner': project?.ownerId === user?.id,
        'Project Participant Count': project?.participantCount
      });
    }
  }, [isOpen, participantsResponse, participants, isSiteProject, chatsResponse, isChatsLoading, project?.ownerId, project?.participantCount, user?.id]);
  
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);
  
  useEffect(() => {
    if (isOpen) {
      setIsVisible(false);
      setIsMounted(true);
      const t = setTimeout(() => {
        setIsVisible(true);
      }, 24);
      return () => {
        clearTimeout(t);
      };
    } else {
      setIsVisible(false);
      const t = setTimeout(() => setIsMounted(false), 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (!isMounted) return null;

  const getInitials = (participant) => {
    const firstName = participant.firstName || '';
    const lastName = participant.lastName || '';
    const username = participant.username || participant.email || '';
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (username) {
      return username.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  const getDisplayName = (participant) => {
    if (!participant) return 'Unknown User';
    
    const firstName = participant.firstName || '';
    const lastName = participant.lastName || '';
    const username = participant.username || participant.email || 'Anonymous';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    }
    return username;
  };

  const modalContent = (
    <div className="fixed inset-0 z-[50001]" role="dialog" aria-modal="true" aria-labelledby="participants-modal-title">
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black transition-opacity duration-200 ease-out ${isVisible ? 'bg-opacity-50 opacity-100' : 'bg-opacity-0 opacity-0'}`} onClick={onClose}></div>
      
      {/* Modal panel */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 pointer-events-none">
        <div className={`relative flex flex-col w-full h-full sm:h-auto sm:max-w-lg sm:w-full overflow-hidden bg-white dark:bg-gray-800 shadow-xl transition-all duration-200 ease-out sm:rounded-lg transform pointer-events-auto ${isVisible ? 'translate-y-0 sm:scale-100 opacity-100' : 'translate-y-full sm:translate-y-0 sm:scale-95 opacity-0'}`}>
          {/* Header */}
          <div className="sticky top-0 z-10 border-b border-gray-200 dark:border-gray-600/50 bg-white dark:bg-gray-800">
            <div
              className="flex items-center justify-between p-4"
              style={{
                paddingTop: 'calc(var(--app-safe-area-top) + 1rem)',
              }}
            >
              <div className="flex items-center space-x-2">
                {/* {isCompetitive ? (
                  <Trophy className="w-5 h-5 text-orange-500" />
                ) : isEndless ? (
                  <Award className="w-5 h-5 text-green-500" />
                ) : (
                  <Users className="w-5 h-5 text-blue-500" />
                )} */}
                <h3 id="participants-modal-title" className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                  Participants
                  {/* {isCompetitive ? 'Progress Overview' : isEndless ? 'Participants' : 'Team Members'}  */}
                  {/* <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">({participants.length})</span> */}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="rounded-md p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* <div className="px-4 pb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {participants.length} {participants.length === 1 ? 'participant' : 'participants'} in this project
              </p>
            </div> */}
          </div>
          
          {/* Invites - Show for ongoing projects */}
          {project?.status === 'ongoing' && (
            <div className="px-4 pt-4 pb-3 border-b border-gray-200 dark:border-gray-600/50 space-y-3">
              {/* "Join my run": companion QR + link, front and center — the
                  invite that works for people standing next to you (guests
                  are minted automatically, no account needed) */}
              <CompanionJoinPanel projectId={projectId} projectName={project?.name} />
              {/* Per-user privacy: live position on the group map. Only shown
                  once we know the user's own chat on this run. */}
              {myChat && (
                <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-xl space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-900 dark:text-white">
                          Show my position to the group
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          Companions see your pin on the shared map
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={shareLocation}
                      onClick={toggleShareLocation}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                        shareLocation ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          shareLocation ? 'translate-x-[18px]' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                  {shareLocation && hasGenericName && (
                    <div className="flex items-center gap-2 pl-6">
                      <input
                        type="text"
                        value={mapName}
                        onChange={(e) => setMapName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && saveMapName()}
                        placeholder="Your name on the map"
                        maxLength={30}
                        className="flex-1 min-w-0 px-2.5 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                      <button
                        type="button"
                        onClick={saveMapName}
                        disabled={!mapName.trim() || savingName}
                        className="px-3 py-1.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                      >
                        {savingName ? '…' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Team card (member strip + username invite) is B2C machinery —
                  irrelevant noise for Scan-to-Play guests, whose only invite
                  gesture is the QR above */}
              {user?.authProvider !== 'guest' && (
                <InviteTeamCard
                  project={project}
                  projectId={projectId}
                  isOwner={project?.ownerId === user?.id}
                  onTeamUpdate={() => {
                    // Refetch participants and leaderboard after adding member
                    queryClient.invalidateQueries(['participants', projectId]);
                    queryClient.invalidateQueries(['leaderboard', projectId]);
                    queryClient.invalidateQueries(createQueryKey.project(projectId));
                  }}
                />
              )}
            </div>
          )}
          
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading || isLeaderboardLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : participants.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No participants yet</p>
              </div>
            ) : !isSiteProject && leaderboard.length > 0 ? (
              /* Competitive/Event modes: Show leaderboard with rankings */
              <div className="space-y-3">
                {/* Header indicating these are currently online members */}
                <div className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    Currently Online ({leaderboard.length})
                  </h4>
                </div>
                {leaderboard.map((standing, index) => {
                  const rank = standing.rank || index + 1;
                  const isFirst = rank === 1;
                  const isSecond = rank === 2;
                  const isThird = rank === 3;
                  
                  const getRankDisplay = () => {
                    return (
                      <span className={`text-sm font-semibold w-6 text-center ${
                        isFirst ? 'text-blue-600 dark:text-blue-400' : 
                        isSecond ? 'text-gray-600 dark:text-gray-400' : 
                        isThird ? 'text-gray-500 dark:text-gray-500' : 
                        'text-gray-400 dark:text-gray-600'
                      }`}>
                        #{rank}
                      </span>
                    );
                  };

                  // Get objectives from current project (fresh data with latest isCompleted values)
                  const projectObjectives = currentProject?.objectives ?? [];
                  const totalObjectives = standing.totalObjectives ?? projectObjectives.length ?? 0;
                  
                  // For ParticipantsModal, always use project-level completion (isCompleted flag)
                  // This shows the overall project state, not individual user progress
                  // Site projects are inherently collaborative, and even competitive projects show shared objectives
                  const completedCount = projectObjectives.filter(obj => obj.isCompleted === true).length;
                  const completionPercent = totalObjectives > 0 ? Math.round((completedCount / totalObjectives) * 100) : 0;

                  return (
                    <div
                      key={standing.userId || index}
                      className={`relative p-4 rounded-lg border transition-all ${
                        isFirst && isCompetitive
                          ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Rank */}
                        <div className="flex-shrink-0 pt-1">
                          {getRankDisplay()}
                        </div>

                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          {standing.user?.avatar ? (
                            <img
                              src={getImageUrl(standing.user.avatar)}
                              alt={getDisplayName(standing.user)}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {getInitials(standing.user || {})}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* User info and stats */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {getDisplayName(standing.user)}
                            </p>
                            {standing.user?.role === 'ADMIN' && (
                              <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                            )}
                          </div>

                          {/* Progress bar - overall completion */}
                          {totalObjectives > 0 && (
                            <div className="mb-2">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-gray-600 dark:text-gray-400">
                                  {completedCount} of {totalObjectives} completed
                                          </span>
                                <span className="text-gray-500 dark:text-gray-500 font-medium">
                                  {completionPercent}%
                                          </span>
                                        </div>
                                        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                          <div
                                  className={`h-full transition-all ${
                                    completionPercent === 100 ? 'bg-green-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${completionPercent}%` }}
                                          />
                                        </div>
                                      </div>
                          )}

                          {/* Objectives checklist - simplified */}
                          {currentProject?.objectives && currentProject.objectives.length > 0 && (
                            <div className="space-y-1.5 mb-2">
                              {currentProject.objectives
                                .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
                                .map((objective, objIdx) => {
                                  // Always use project-level completion for display in ParticipantsModal
                                  // This shows the overall project state (shared objectives)
                                  const isUserCompleted = objective.isCompleted === true;

                                  return (
                                    <div key={objIdx} className="flex items-center gap-2 text-xs">
                                      {isUserCompleted ? (
                                        <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                      ) : (
                                        <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                      )}
                                      <span className={`flex-1 truncate ${
                                        isUserCompleted
                                          ? 'text-gray-500 dark:text-gray-500 line-through'
                                          : 'text-gray-700 dark:text-gray-300'
                                      }`}>
                                        {objective.objectiveName}
                                      </span>
                                    </div>
                                  );
                              })}
                            </div>
                          )}

                          {/* Additional stats */}
                          {standing.totalCaptures > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {standing.totalCaptures} contribution{standing.totalCaptures !== 1 ? 's' : ''}
                          </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Site/Collaborative: roster with per-person presence dots */
              <div className="space-y-2">
                <div className="mb-4 pb-0 border-b-0 border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    Participants ({participants.length})
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {onlineCount} online now
                  </p>
                </div>
                {participants.map((participant, index) => (
                  <div
                    key={participant.id || index}
                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {/* Avatar */}
                    <div className="relative">
                      {participant.avatar ? (
                        <img
                          src={getImageUrl(participant.avatar)}
                          alt={getDisplayName(participant)}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">
                            {getInitials(participant)}
                          </span>
                        </div>
                      )}
                      {/* Presence dot: green = connected right now, grey = on
                          the run but currently unreachable (locked phone, dead
                          zone). Being offline does NOT drop them from the list. */}
                      <div
                        className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-white dark:border-gray-800 rounded-full ${
                          participant.isOnline ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-500'
                        }`}
                        title={participant.isOnline ? 'Online' : 'Away'}
                      ></div>
                    </div>
                    
                    {/* User info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {getDisplayName(participant)}
                        </p>
                        {participant.role === 'ADMIN' && (
                          <Crown className="w-4 h-4 text-amber-600 dark:text-amber-500 flex-shrink-0" title="Project Owner" />
                        )}
                      </div>
                      {participant.username && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          @{participant.username}
                          {!participant.isOnline && (
                            <span className="ml-1.5 text-gray-400 dark:text-gray-500">· away</span>
                          )}
                        </p>
                      )}
                    </div>
                    
                    {/* Role badge */}
                    <div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        participant.role === 'ADMIN' 
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
                      }`}>
                        {participant.role === 'ADMIN' ? 'Owner' : 'Member'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="sticky bottom-0 border-t border-gray-200 dark:border-gray-600/50 bg-white dark:bg-gray-800 px-4 py-3 flex justify-end items-center min-h-[56px]" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
            <button
              onClick={onClose}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ParticipantsModal;
